import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Tag} from 'primereact/tag'
import React, {forwardRef, useImperativeHandle, useRef} from 'react'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import {useListUserWithRoles} from '../../healthcare/query-keys/HealthCareQueryManager'
import StyledDropdown from '../components/StyledDropdown'
import StyledMultiSelect from '../components/StyledMultiSelect'

const StyledHCPractitionerOptions = forwardRef(
    (
        {
            value = null,
            onChange = () => {},
            placeholder = 'Select Health Care Practitioner',

            hasPanelFooter = true,
            optionType = component.dropdownGroup,
        },
        ref,
    ) => {
        useSignals()
        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
            changeValue: newValue => handleChangeValue(newValue),

            changeHospitalId: newHospitalId => {
                hospitalId.value = newHospitalId
                optionRef.current?.changeValue(null)
            },
        }))

        //#region Context
        const {user} = useAuth()
        const {userRole} = useAppConfig()
        const {currentHospital} = useUserHospital()
        // const {}
        //#endregion

        //#region Value
        const hospitalId = useSignal(
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        )
        const optionRef = useRef(null)

        const handleGetValue = () => {
            return optionRef.current?.getValue()
        }

        const handleChangeValue = newValue => {
            optionRef.current?.changeValue(newValue)
        }
        //#endregion

        //#region Dropdown
        const {data: options} = useListUserWithRoles(
            user?.id,
            true,
            null,
            {hospitalId: hospitalId.value},
            !!user?.id && !!hospitalId.value,
        )

        const valueTemplate = (option, props) => {
            if (option) {
                return (
                    <div className="flex align-items-center">
                        {option.extraData ? (
                            <img
                                alt={option.label}
                                src={option.extraData}
                                className={`mr-2`}
                                style={{width: '48px', height: '20px'}}
                            />
                        ) : (
                            <i
                                className="pi pi-home mr-2"
                                style={{fontSize: '16px'}}></i>
                        )}

                        <div className="font-bold">{option.label}</div>
                    </div>
                )
            }

            return <span>{props.placeholder}</span>
        }

        const itemTemplate = option => {
            return (
                <div className="w-full flex align-items-center justify-content-between hover:text-primary">
                    <div className="flex align-items-center mr-5">
                        <div
                            className="flex align-items-center justify-content-center mr-2"
                            style={{width: '48px'}}>
                            {option.extraData ? (
                                <img
                                    alt={option.label}
                                    src={option.extraData}
                                    className={`mr-2`}
                                    style={{width: '100%', height: '24px'}}
                                />
                            ) : (
                                <div>
                                    <i
                                        className="pi pi-home"
                                        style={{fontSize: '20px'}}></i>
                                </div>
                            )}
                        </div>
                        <div className="font-bold">
                            <h6 className="m-0 mb-1">{option.label}</h6>
                            <small>{option?.data}</small>
                        </div>
                    </div>

                    <div>
                        <Tag
                            severity={
                                option?.qualification ? 'success' : 'warning'
                            }
                            value={
                                option?.qualification
                                    ? 'Qualified'
                                    : 'Not Qualified'
                            }
                            className="px-3"
                        />
                    </div>
                </div>
            )
        }
        //#endregion

        //#region MultiSelect
        const multiSelectItemTemplate = option => {
            return (
                <div className="w-full flex align-items-center justify-content-between hover:text-primary">
                    <div className="flex align-items-center mr-5">
                        <div
                            className="flex align-items-center justify-content-center mr-2"
                            style={{width: '48px'}}>
                            {option.extraData ? (
                                <img
                                    alt={option.label}
                                    src={option.extraData}
                                    className={`mr-2`}
                                    style={{width: '100%', height: '24px'}}
                                />
                            ) : (
                                <div>
                                    <i
                                        className="pi pi-home"
                                        style={{fontSize: '20px'}}></i>
                                </div>
                            )}
                        </div>
                        <div className="font-bold">
                            <h6 className="m-0 mb-1">{option.label}</h6>
                            <small>{option?.data}</small>
                        </div>
                    </div>

                    <div>
                        <Tag
                            severity={
                                option?.qualification ? 'success' : 'warning'
                            }
                            value={
                                <span>
                                    <i
                                        className={`pi mr-2 ${
                                            option?.qualification
                                                ? 'pi-check-circle'
                                                : ' pi-exclamation-triangle'
                                        }`}></i>
                                    {option?.qualification
                                        ? 'Qualified'
                                        : 'Not Qualified'}
                                </span>
                            }
                            className="px-3"
                        />
                    </div>
                </div>
            )
        }
        //#endregion

        return (
            <React.Fragment>
                {optionType === component.dropdown && (
                    <StyledDropdown
                        ref={optionRef}
                        options={options || []}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        itemTemplate={itemTemplate}
                        valueTemplate={valueTemplate}
                        showClear={true}
                        hasPanelFooter={hasPanelFooter}
                        optionType={component.dropdown}
                    />
                )}

                {optionType === component.multiSelect && (
                    <StyledMultiSelect
                        ref={optionRef}
                        options={options || []}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        itemTemplate={multiSelectItemTemplate}
                        showClear={true}
                    />
                )}
            </React.Fragment>
        )
    },
)
export default StyledHCPractitionerOptions
